import React, {
    useEffect,
    useMemo,
    useState
} from 'react';
import { Link } from 'react-router-dom';
import './adminPortfolio.css';
import getImageUrl from '../../../utils/getImageUrl';
import API_BASE_URL from '../../../config/api';

const categories = [
    '전체',
    'LED 전광판',
    '빔프로젝터',
    '설치',
    '기업행사',
    '전시 · 박람회',
    '종교시설'
];

const ITEMS_PER_PAGE = 10;

const AdminPortfolioList = () => {
    const [portfolios, setPortfolios] =
        useState([]);

    const [selectedCategory, setSelectedCategory] =
        useState('전체');

    const [searchKeyword, setSearchKeyword] =
        useState('');

    const [currentPage, setCurrentPage] =
        useState(1);

    const [isLoading, setIsLoading] =
        useState(true);

    const [error, setError] =
        useState('');

    const [selectedPortfolio, setSelectedPortfolio] =
        useState(null);

    const [isDetailLoading, setIsDetailLoading] =
        useState(false);

    const [detailError, setDetailError] =
        useState('');

    /*
     * =====================================================
     * PORTFOLIO LIST
     * =====================================================
     */

    useEffect(() => {
        const fetchPortfolios = async () => {
            try {
                setIsLoading(true);
                setError('');

                const response = await fetch(
                    `${API_BASE_URL}/api/portfolios`,
                    {
                        method: 'GET',
                        credentials: 'include'
                    }
                );

                const result =
                    await response.json();

                if (!response.ok) {
                    throw new Error(
                        result.message ||
                        '시공사례를 불러오지 못했습니다.'
                    );
                }

                setPortfolios(
                    result.portfolios || []
                );
            } catch (error) {
                console.error(
                    '시공사례 조회 오류:',
                    error
                );

                setError(
                    error.message
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchPortfolios();
    }, []);

    /*
     * =====================================================
     * BODY SCROLL
     * =====================================================
     */

    useEffect(() => {
        if (selectedPortfolio) {
            document.body.style.overflow =
                'hidden';
        } else {
            document.body.style.overflow =
                '';
        }

        return () => {
            document.body.style.overflow =
                '';
        };
    }, [selectedPortfolio]);

    /*
     * =====================================================
     * FILTER + SEARCH
     * =====================================================
     */

    const filteredPortfolios =
        useMemo(() => {
            const keyword =
                searchKeyword
                    .trim()
                    .toLowerCase();

            return portfolios.filter(
                (portfolio) => {
                    const matchesCategory =
                        selectedCategory === '전체' ||
                        portfolio.category === selectedCategory;

                    if (!matchesCategory) {
                        return false;
                    }

                    if (!keyword) {
                        return true;
                    }

                    const searchableText = [
                        portfolio.title,
                        portfolio.category,
                        portfolio.industry,
                        portfolio.product,
                        portfolio.contractor,
                        portfolio.location,
                        portfolio.description,
                        portfolio.work_date
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();

                    return searchableText.includes(
                        keyword
                    );
                }
            );
        }, [
            portfolios,
            selectedCategory,
            searchKeyword
        ]);

    const handleCategoryChange = (
        category
    ) => {
        setSelectedCategory(
            category
        );
        setCurrentPage(1);
    };

    const handleSearchChange = (
        event
    ) => {
        setSearchKeyword(
            event.target.value
        );
        setCurrentPage(1);
    };

    const handleClearSearch = () => {
        setSearchKeyword('');
        setCurrentPage(1);
    };

    /*
     * =====================================================
     * PAGINATION
     * =====================================================
     */

    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredPortfolios.length /
            ITEMS_PER_PAGE
        )
    );

    const paginatedPortfolios =
        useMemo(() => {
            const startIndex =
                (currentPage - 1) *
                ITEMS_PER_PAGE;

            const endIndex =
                startIndex +
                ITEMS_PER_PAGE;

            return filteredPortfolios.slice(
                startIndex,
                endIndex
            );
        }, [
            filteredPortfolios,
            currentPage
        ]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(
                totalPages
            );
        }
    }, [
        currentPage,
        totalPages
    ]);

    const pageNumbers = useMemo(() => {
        if (totalPages <= 5) {
            return Array.from(
                {
                    length: totalPages
                },
                (_, index) =>
                    index + 1
            );
        }

        let startPage = Math.max(
            1,
            currentPage - 2
        );

        let endPage = Math.min(
            totalPages,
            startPage + 4
        );

        if (endPage - startPage < 4) {
            startPage = Math.max(
                1,
                endPage - 4
            );
        }

        return Array.from(
            {
                length:
                    endPage - startPage + 1
            },
            (_, index) =>
                startPage + index
        );
    }, [
        currentPage,
        totalPages
    ]);

    /*
     * =====================================================
     * DATE
     * =====================================================
     */

    const formatDate = (date) => {
        if (!date) {
            return '-';
        }

        return new Intl.DateTimeFormat(
            'ko-KR',
            {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }
        ).format(
            new Date(date)
        );
    };

    /*
     * =====================================================
     * DETAIL
     * =====================================================
     */

    const handleView = async (
        portfolioId
    ) => {
        try {
            setIsDetailLoading(true);
            setDetailError('');
            setSelectedPortfolio(null);

            const response = await fetch(
                `${API_BASE_URL}/api/portfolios/${portfolioId}`,
                {
                    method: 'GET',
                    credentials: 'include'
                }
            );

            const result =
                await response.json();

            if (!response.ok) {
                throw new Error(
                    result.message ||
                    '시공사례를 불러오지 못했습니다.'
                );
            }

            setSelectedPortfolio(
                result.portfolio
            );
        } catch (error) {
            console.error(
                '시공사례 상세 조회 오류:',
                error
            );

            setDetailError(
                error.message
            );
        } finally {
            setIsDetailLoading(false);
        }
    };

    const handleCloseDetail = () => {
        setSelectedPortfolio(null);
        setDetailError('');
    };

    /*
     * =====================================================
     * DELETE
     * =====================================================
     */

    const handleDelete = async (
        portfolioId,
        title
    ) => {
        const confirmed = window.confirm(
            `"${title}" 시공사례를 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.`
        );

        if (!confirmed) {
            return;
        }

        try {
            const response = await fetch(
                `${API_BASE_URL}/api/portfolios/${portfolioId}`,
                {
                    method: 'DELETE',
                    credentials: 'include'
                }
            );

            const result =
                await response.json();

            if (!response.ok) {
                throw new Error(
                    result.message ||
                    '시공사례 삭제에 실패했습니다.'
                );
            }

            setPortfolios((prev) =>
                prev.filter(
                    (portfolio) =>
                        portfolio.portfolio_id !==
                        portfolioId
                )
            );

            if (
                selectedPortfolio?.portfolio_id ===
                portfolioId
            ) {
                handleCloseDetail();
            }

            alert(
                '시공사례가 삭제되었습니다.'
            );
        } catch (error) {
            console.error(
                '시공사례 삭제 오류:',
                error
            );

            alert(
                error.message
            );
        }
    };

    const hasSearchOrFilter =
        selectedCategory !== '전체' ||
        searchKeyword.trim() !== '';

    return (
        <div className="admin-portfolio">
            {/* HEADER */}
            <div className="admin-page-header">
                <div>
                    <p>
                        PORTFOLIO
                    </p>

                    <h1>
                        시공사례 관리
                    </h1>

                    <span>
                        홈페이지에 노출되는 시공사례를 관리합니다.
                    </span>
                </div>

                <Link
                    to="/admin/portfolios/new"
                    className="admin-portfolio-add"
                >
                    <i className="mdi mdi-plus"></i>
                    시공사례 등록
                </Link>
            </div>

            {/* CONTENT */}
            <div className="admin-portfolio-content">
                {/* CATEGORY + SEARCH */}
                <div className="admin-portfolio-toolbar">
                    <div className="admin-portfolio-tabs">
                        {categories.map(
                            (category) => (
                                <button
                                    key={category}
                                    type="button"
                                    className={
                                        selectedCategory ===
                                        category
                                            ? 'active'
                                            : ''
                                    }
                                    onClick={() =>
                                        handleCategoryChange(
                                            category
                                        )
                                    }
                                >
                                    {category}
                                </button>
                            )
                        )}
                    </div>

                    <div className="admin-portfolio-search-area">
                        <span className="admin-portfolio-result-count">
                            {filteredPortfolios.length}건
                        </span>

                        <div className="admin-portfolio-search">
                            <i className="mdi mdi-magnify"></i>

                            <input
                                type="text"
                                value={searchKeyword}
                                onChange={handleSearchChange}
                                placeholder="제목, 장소, 업종, 제품 검색"
                                aria-label="시공사례 검색"
                            />

                            {searchKeyword && (
                                <button
                                    type="button"
                                    className="admin-portfolio-search-clear"
                                    onClick={handleClearSearch}
                                    aria-label="검색어 지우기"
                                >
                                    <i className="mdi mdi-close"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* LOADING */}
                {isLoading ? (
                    <div className="admin-portfolio-empty">
                        <i className="mdi mdi-loading mdi-spin"></i>

                        <strong>
                            시공사례를 불러오는 중입니다.
                        </strong>
                    </div>
                ) : error ? (
                    /* ERROR */
                    <div className="admin-portfolio-empty">
                        <i className="mdi mdi-alert-circle-outline"></i>

                        <strong>
                            시공사례를 불러오지 못했습니다.
                        </strong>

                        <p>
                            {error}
                        </p>
                    </div>
                ) : filteredPortfolios.length === 0 ? (
                    /* EMPTY */
                    <div className="admin-portfolio-empty">
                        <i className="mdi mdi-magnify"></i>

                        <strong>
                            {hasSearchOrFilter
                                ? '검색 결과가 없습니다.'
                                : '등록된 시공사례가 없습니다.'
                            }
                        </strong>

                        <p>
                            {hasSearchOrFilter
                                ? '검색어 또는 카테고리를 변경해보세요.'
                                : '새로운 시공사례를 등록해주세요.'
                            }
                        </p>
                    </div>
                ) : (
                    <>
                        {/* TABLE */}
                        <div className="admin-portfolio-table-wrap">
                            <table className="admin-portfolio-table">
                                <thead>
                                    <tr>
                                        <th>
                                            대표 이미지
                                        </th>

                                        <th>
                                            제목
                                        </th>

                                        <th>
                                            카테고리
                                        </th>

                                        <th>
                                            장소
                                        </th>

                                        <th>
                                            시공일
                                        </th>

                                        <th>
                                            공개 상태
                                        </th>

                                        <th>
                                            관리
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {paginatedPortfolios.map(
                                        (portfolio) => (
                                            <tr
                                                key={
                                                    portfolio.portfolio_id
                                                }
                                            >
                                                {/* THUMBNAIL */}
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="admin-portfolio-thumbnail admin-portfolio-thumbnail-button"
                                                        onClick={() =>
                                                            handleView(
                                                                portfolio.portfolio_id
                                                            )
                                                        }
                                                        aria-label={`${portfolio.title} 상세보기`}
                                                    >
                                                        <img
                                                            src={
                                                                getImageUrl(portfolio.thumbnail_image)
                                                            }
                                                            alt={
                                                                portfolio.title
                                                            }
                                                        />
                                                    </button>
                                                </td>

                                                {/* TITLE */}
                                                <td>
                                                    <div className="admin-portfolio-title">
                                                        <button
                                                            type="button"
                                                            className="admin-portfolio-title-button"
                                                            onClick={() =>
                                                                handleView(
                                                                    portfolio.portfolio_id
                                                                )
                                                            }
                                                        >
                                                            {portfolio.title}
                                                        </button>

                                                        <span>
                                                            등록일{' '}
                                                            {formatDate(
                                                                portfolio.created_at
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* CATEGORY */}
                                                <td>
                                                    <span className="admin-portfolio-category">
                                                        {portfolio.category}
                                                    </span>
                                                </td>

                                                {/* LOCATION */}
                                                <td>
                                                    {portfolio.location ||
                                                        '-'
                                                    }
                                                </td>

                                                {/* DATE */}
                                                <td>
                                                    {formatDate(
                                                        portfolio.work_date
                                                    )}
                                                </td>

                                                {/* STATUS */}
                                                <td>
                                                    <span
                                                        className={
                                                            portfolio.is_active
                                                                ? 'admin-portfolio-status active'
                                                                : 'admin-portfolio-status inactive'
                                                        }
                                                    >
                                                        {portfolio.is_active
                                                            ? '공개'
                                                            : '비공개'
                                                        }
                                                    </span>
                                                </td>

                                                {/* ACTION */}
                                                <td>
                                                    <div className="admin-portfolio-actions">
                                                        <button
                                                            type="button"
                                                            className="admin-portfolio-view"
                                                            onClick={() =>
                                                                handleView(
                                                                    portfolio.portfolio_id
                                                                )
                                                            }
                                                        >
                                                            보기
                                                        </button>

                                                        <Link
                                                            to={
                                                                `/admin/portfolios/${portfolio.portfolio_id}/edit`
                                                            }
                                                            className="admin-portfolio-edit"
                                                        >
                                                            수정
                                                        </Link>

                                                        <button
                                                            type="button"
                                                            className="admin-portfolio-delete"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    portfolio.portfolio_id,
                                                                    portfolio.title
                                                                )
                                                            }
                                                        >
                                                            삭제
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* PAGINATION */}
                        {totalPages > 1 && (
                            <div className="admin-portfolio-pagination">
                                <button
                                    type="button"
                                    className="admin-pagination-arrow"
                                    onClick={() =>
                                        setCurrentPage(
                                            (prev) =>
                                                Math.max(
                                                    prev - 1,
                                                    1
                                                )
                                        )
                                    }
                                    disabled={
                                        currentPage === 1
                                    }
                                    aria-label="이전 페이지"
                                >
                                    <i className="mdi mdi-chevron-left"></i>
                                </button>

                                <div className="admin-pagination-pages">
                                    {pageNumbers.map(
                                        (page) => (
                                            <button
                                                key={page}
                                                type="button"
                                                className={
                                                    currentPage === page
                                                        ? 'active'
                                                        : ''
                                                }
                                                onClick={() =>
                                                    setCurrentPage(
                                                        page
                                                    )
                                                }
                                            >
                                                {page}
                                            </button>
                                        )
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className="admin-pagination-arrow"
                                    onClick={() =>
                                        setCurrentPage(
                                            (prev) =>
                                                Math.min(
                                                    prev + 1,
                                                    totalPages
                                                )
                                        )
                                    }
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                    aria-label="다음 페이지"
                                >
                                    <i className="mdi mdi-chevron-right"></i>
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* DETAIL LOADING */}
            {isDetailLoading && (
                <div className="admin-portfolio-modal-overlay">
                    <div className="admin-portfolio-modal-loading">
                        <i className="mdi mdi-loading mdi-spin"></i>

                        <strong>
                            시공사례를 불러오는 중입니다.
                        </strong>
                    </div>
                </div>
            )}

            {/* DETAIL ERROR */}
            {!isDetailLoading &&
                detailError && (
                    <div
                        className="admin-portfolio-modal-overlay"
                        onClick={handleCloseDetail}
                    >
                        <div
                            className="admin-portfolio-modal-loading"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            <i className="mdi mdi-alert-circle-outline"></i>

                            <strong>
                                시공사례를 불러오지 못했습니다.
                            </strong>

                            <p>
                                {detailError}
                            </p>

                            <button
                                type="button"
                                onClick={handleCloseDetail}
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                )}

            {/* DETAIL MODAL */}
            {!isDetailLoading &&
                selectedPortfolio && (
                    <div
                        className="admin-portfolio-modal-overlay"
                        onClick={handleCloseDetail}
                    >
                        <div
                            className="admin-portfolio-modal"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            {/* MODAL HEADER */}
                            <div className="admin-portfolio-modal-header">
                                <div>
                                    <p>
                                        PORTFOLIO DETAIL
                                    </p>

                                    <h2>
                                        {selectedPortfolio.title}
                                    </h2>
                                </div>

                                <button
                                    type="button"
                                    className="admin-portfolio-modal-close"
                                    onClick={handleCloseDetail}
                                    aria-label="닫기"
                                >
                                    <i className="mdi mdi-close"></i>
                                </button>
                            </div>

                            {/* MODAL BODY */}
                            <div className="admin-portfolio-modal-body">
                                {/* REPRESENTATIVE IMAGE */}
                                <div className="admin-portfolio-modal-main-image">
                                    <img
                                        src={
                                            getImageUrl(selectedPortfolio.thumbnail_image)
                                        }
                                        alt={
                                            selectedPortfolio.title
                                        }
                                    />
                                </div>

                                {/* INFORMATION */}
                                <div className="admin-portfolio-modal-info">
                                    <DetailRow
                                        label="카테고리"
                                        value={
                                            selectedPortfolio.category
                                        }
                                    />

                                    <DetailRow
                                        label="업종"
                                        value={
                                            selectedPortfolio.industry
                                        }
                                    />

                                    <DetailRow
                                        label="설치제품"
                                        value={
                                            selectedPortfolio.product
                                        }
                                    />

                                    <DetailRow
                                        label="시공사"
                                        value={
                                            selectedPortfolio.contractor
                                        }
                                    />

                                    <DetailRow
                                        label="장소"
                                        value={
                                            selectedPortfolio.location
                                        }
                                    />

                                    <DetailRow
                                        label="시공일"
                                        value={
                                            formatDate(
                                                selectedPortfolio.work_date
                                            )
                                        }
                                    />

                                    <DetailRow
                                        label="공개 상태"
                                        value={
                                            selectedPortfolio.is_active
                                                ? '공개'
                                                : '비공개'
                                        }
                                    />

                                    <DetailRow
                                        label="정렬 순서"
                                        value={
                                            selectedPortfolio.sort_order
                                        }
                                    />
                                </div>

                                {/* DESCRIPTION */}
                                <div className="admin-portfolio-modal-description">
                                    <strong>
                                        시공내용
                                    </strong>

                                    <p>
                                        {selectedPortfolio.description ||
                                            '등록된 시공 내용이 없습니다.'
                                        }
                                    </p>
                                </div>

                                {/* DETAIL IMAGES */}
                                {selectedPortfolio.detail_images &&
                                    selectedPortfolio.detail_images.length > 0 && (
                                        <div className="admin-portfolio-modal-gallery">
                                            <div className="admin-portfolio-modal-section-title">
                                                <strong>
                                                    상세 이미지
                                                </strong>

                                                <span>
                                                    {selectedPortfolio.detail_images.length}장
                                                </span>
                                            </div>

                                            <div className="admin-portfolio-modal-gallery-grid">
                                                {selectedPortfolio.detail_images.map(
                                                    (
                                                        image,
                                                        index
                                                    ) => (
                                                        <div
                                                            className="admin-portfolio-modal-gallery-item"
                                                            key={
                                                                image.portfolio_image_id
                                                            }
                                                        >
                                                            <img
                                                                src={
                                                                    getImageUrl(image.image_url)
                                                                }
                                                                alt={
                                                                    `${selectedPortfolio.title} 상세 이미지 ${index + 1}`
                                                                }
                                                            />
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}
                            </div>

                            {/* MODAL FOOTER */}
                            <div className="admin-portfolio-modal-footer">
                                {selectedPortfolio.is_active && (
                                    <Link
                                        to={
                                            `/portfolio/${selectedPortfolio.portfolio_id}`
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                        className="admin-portfolio-modal-public"
                                    >
                                        실제 페이지 보기
                                        <i className="mdi mdi-open-in-new"></i>
                                    </Link>
                                )}

                                <Link
                                    to={
                                        `/admin/portfolios/${selectedPortfolio.portfolio_id}/edit`
                                    }
                                    className="admin-portfolio-modal-edit"
                                >
                                    수정하기
                                </Link>

                                <button
                                    type="button"
                                    className="admin-portfolio-modal-cancel"
                                    onClick={handleCloseDetail}
                                >
                                    닫기
                                </button>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};

const DetailRow = ({
    label,
    value
}) => {
    return (
        <div className="admin-portfolio-modal-info-row">
            <strong>
                {label}
            </strong>

            <span>
                {value !== null &&
                value !== undefined &&
                value !== ''
                    ? value
                    : '-'
                }
            </span>
        </div>
    );
};

export default AdminPortfolioList;
